/**
 * Editable site content. Talent (hosts/heroes) and prize copy are announced
 * over the campaign — update these constants and redeploy (decision: content
 * is repo-managed, not admin-editable). A `null` name renders the designed
 * "COMING SOON" treatment.
 */

// Type-only: erased at build, so this cannot create a runtime import cycle
// with eventState.ts (which imports PROMO_BANNER from here).
import type { EventPhase } from '../lib/eventState';

export const TWITCH_CHANNEL_URL = 'https://twitch.tv/hyperx';

/**
 * Before the show, the Twitch CTA deep-links to the scheduled segment so a
 * visitor can set a reminder rather than land on whatever is streaming
 * (HX-44). The segment link stops being useful the moment the show starts, so
 * LIVE and POST both fall back to the channel itself.
 */
export const TWITCH_SCHEDULE_URL =
  'https://www.twitch.tv/hyperx/schedule?segmentID=9a7787eb-e81d-4e50-ba26-4937108cfd55';

/**
 * Where EVERY watch surface points — Twitch button, hero WATCH heading, docked
 * bar, and the red WATCH NOW CTA. One resolver so the phase rule can't drift
 * between them (client 2026-08-17):
 *
 *   PRE        the HX-44 scheduled-segment deep link, ALWAYS. The admin URL is
 *              deliberately ignored here: before the show there is nothing to
 *              watch, and a reminder beats dropping someone on a dark channel.
 *   LIVE/POST  the admin's single `watchUrl` from event_config when set — the
 *              PM repoints that one field to the VOD once the broadcast ends —
 *              and the channel as a fallback, so no surface is ever dead.
 */
export function watchUrlForPhase(phase: EventPhase, adminWatchUrl: string | null): string {
  if (phase === 'PRE') return TWITCH_SCHEDULE_URL;
  return adminWatchUrl ?? TWITCH_CHANNEL_URL;
}

/**
 * Enter-to-Win CTA visibility. ON per client 2026-07-15 as a display-only
 * element — it links nowhere until the registration flow ships (M2, HX-8);
 * see EnterToWinButton in HeroSection.tsx.
 */
export const ENTER_TO_WIN_ENABLED = false;

/**
 * Hosts are baked into the triangle artwork (frames/hosts-full-lite.svg) — the
 * names here feed the accessible label and must match the art, in the art's
 * reading order (top-left, top-right, bottom). A host change needs a designer
 * re-export (decision 2026-07-15: keep baked).
 * 2026-08-12: full lineup announced (HX-41) — Emiru fills the slot that had
 * been COMING SOON since Extra Emily was removed 2026-07-31.
 */
export const HOSTS = ['Will Neff', 'Emiru', 'Fuslie'];

/** One cell of the 6-cell neon grid (CellGrid); null renders COMING SOON. */
export type GridCell = { name: string; image: string } | null;

/**
 * Heroes grid, reading order [top-left, top-mid, top-right, bottom-left,
 * bottom-mid, bottom-right] (desktop 3×2; mobile flows the same order into
 * 2×3). To announce a hero: drop the grayscale photo in
 * design-src/originals/assets/images/players/, add it to
 * scripts/optimize-images.mjs, run `npm run optimize-images`, and replace
 * the null with the emitted .webp path.
 */
export const HEROES: GridCell[] = [
  { name: 'TinaKitten', image: '/assets/images/players/tinakitten.webp' },
  { name: 'XYAA', image: '/assets/images/players/xyaa.webp' },
  { name: 'Tyler1', image: '/assets/images/players/tyler1.webp' },
  { name: 'Agentoo', image: '/assets/images/players/agentoo.webp' },
  { name: 'JeyJeyBink', image: '/assets/images/players/jeyjeybink.webp' },
  { name: 'Skain', image: '/assets/images/players/skain.webp' },
];

/**
 * Product grid (live/post) — same 6-cell frame, images only. `name` is the
 * image alt text. Same reading order as HEROES.
 * 2026-08-13: the six final prize shots landed as one container export
 * (design-src/Products.svg); each cell's photo was cut from it at the cell
 * rect, so the framing is the designer's and object-cover is a 1:1 map. This
 * replaces the interim set of 3 products shown twice.
 */
export const PRIZES: GridCell[] = [
  { name: 'HyperX gaming desktop PC', image: '/assets/images/products/gaming-pc.webp' },
  { name: 'HyperX curved ultrawide gaming monitor', image: '/assets/images/products/monitor.webp' },
  { name: 'HP OmniBook X laptop', image: '/assets/images/products/omnibook-laptop.webp' },
  { name: 'HyperX gaming headset', image: '/assets/images/products/headset.webp' },
  { name: 'HP OMEN 16 gaming laptop', image: '/assets/images/products/omen-laptop.webp' },
  { name: 'HyperX wireless gaming mouse', image: '/assets/images/products/mouse.webp' },
];

/**
 * "Join HyperX" callout (designer addition 2026-07-17): the popup black
 * frame (1200 max × 210) holding two centered paragraphs — first bold.
 * Placement is phase-specific (see HomePage); the copy is identical on all
 * three states. `lines` are the design's exact desktop line breaks —
 * enforced at lg+ by JoinCallout, natural wrap below.
 */
export const JOIN_CALLOUT = {
  paragraphs: [
    {
      bold: true,
      lines: [
        'Join HyperX for Level Awakening, an epic journey filled with legendary battles,',
        'unexpected twists, and exclusive reveals.',
      ],
    },
    {
      bold: false,
      lines: [
        'Together, we forge a new chapter where strength comes not from standing alone, but from',
        'rising together.',
      ],
    },
  ],
};

/**
 * "This is your call to rise" body — the popup-box treatment from the 7.15
 * dev frames, re-flowed to the wide column of the 7.17 revision: each
 * paragraph's `lines` are the design's exact desktop breaks (only the
 * saga paragraph wraps — everything else is a single line), enforced at
 * lg+ by CallToRise and wrapped naturally below, matching JOIN_CALLOUT.
 * `big` renders the frame's bold 24px emphasis line; **bold** spans are
 * honored inside lines.
 *
 * `watchCta` is the LIVE/POST "WATCH NOW" CTA added 2026-08-17 (HX-48, Figma
 * 4552:516). It does not sit BELOW the copy — the design drops it into the
 * tune-in line's slot, so the paragraph carrying `preShowOnly` is the one it
 * replaces once the show starts. Keeping that relationship in the data is what
 * stops the two from ever both rendering (a countdown line under a live
 * stream link) or both vanishing.
 */
export const CALL_TO_RISE = {
  heading: 'THIS IS YOUR CALL TO RISE',
  watchCta: 'WATCH NOW',
  paragraphs: [
    {
      lines: [
        'Last year, champions reforged the Blade of Balance and restored order to the realm.',
      ],
    },
    { lines: ['Now, a new threat emerges.'], big: true },
    {
      lines: [
        '**Level Awakening** is the next chapter in the HyperX Level saga, a live adventure',
        'filled with legendary battles, exclusive reveals, and secrets waiting to be uncovered.',
      ],
    },
    {
      lines: ['Tune in August 20 at 3 PM PT and witness the beginning of a new chapter.'],
      preShowOnly: true,
    },
    { lines: ['**The Awakening has begun.**'] },
  ] as Array<{ lines: string[]; big?: boolean; preShowOnly?: boolean }>,
};

/**
 * HP gaming promo banner (HX-42), shown under the hosts triangle in every
 * phase while the scheduled window is open (HX-43 — the window itself is
 * admin-configurable in event_config, NOT here; the server decides
 * visibility and hands the page a single `bannerVisible` flag).
 *
 * Copy is the client's PSD verbatim. `Terms and conditions apply` is
 * deliberately PLAIN TEXT, not a link (client 2026-08-12): this is HP's
 * promotion, so pointing it at our /terms would misstate whose terms
 * govern.
 *
 * `href` is a fallback only — the live URL comes from event_config so
 * marketing can repoint it without a deploy.
 */
export const PROMO_BANNER = {
  heading: 'Limited time offer.',
  body: 'Power up your setup with 25% off select gaming PCs, displays, and peripherals.',
  codeLabel: 'Use code at checkout:',
  code: 'AWAKENING25',
  cta: 'Shop Now',
  href: 'https://www.hp.com/us-en/shop/slp/hp-gaming',
  finePrint: 'Terms and conditions apply',
  /** Product art alt — the panel is decorative product photography. */
  imageAlt: 'HP and HyperX gaming PC, monitor, laptop, and headset',
};

export const AIR_DATE_LABEL = 'AUGUST 20, 2026';
/** Rendered directly under the date in the countdown hero (client 2026-07-17). */
export const AIR_TIME_LABEL = 'AT 3 PM PT';
